import { type CelebrationIntensity, DEFAULT_CELEBRATIONS } from '@ergo/shared';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';

/**
 * Les récompenses : cœurs qui montent depuis le doigt, pétales qui tombent une fois,
 * toast qui dit ce qui vient d'être fait (docs/redesign-plus-douce.md, §Animations).
 *
 * Cœurs et pétales sont posés dans le DOM à la main, hors de React. Ce sont des
 * décorations : elles ne portent aucun état de l'app, personne ne les lit, elles
 * s'effacent seules — les faire vivre dans un état React obligerait chaque écran à
 * porter une couche et un provider pour un effet qui n'appartient à aucun d'eux.
 * Ici, `hearts(x, y)` se rappelle depuis n'importe quel gestionnaire, en une ligne.
 */

const HEART_COLORS = ['#e08aa2', '#6b86c9', '#e9b949', '#d4926a'];
const PETAL_COLORS = [...HEART_COLORS, '#86a878'];

/** Les palettes que le geste choisit : le vert du paiement, le jaune du pointage. */
export const CELEBRATION_PALETTES = {
  paiement: ['#86a878', '#e08aa2', '#6b86c9'],
  pointage: ['#e9b949', '#e08aa2', '#6b86c9'],
} as const;

/**
 * « Douce » ou « joueuse » (Réglages). Gardé ici en clair plutôt que dans un contexte :
 * les fonctions ci-dessous s'appellent depuis un gestionnaire d'événement, pas depuis
 * un rendu, et n'ont donc aucun moyen de lire un contexte.
 */
let intensity: CelebrationIntensity = DEFAULT_CELEBRATIONS;

export function setCelebrationIntensity(next: CelebrationIntensity): void {
  intensity = next;
}

/**
 * Le réglage suit la praticienne d'un appareil à l'autre : il vient du serveur. Une
 * lecture au montage de la coquille suffit — Réglages met la valeur à jour en direct
 * quand elle l'enregistre.
 */
export function useCelebrationSetting(): void {
  useEffect(() => {
    void api.settings
      .get()
      .then((settings) => setCelebrationIntensity(settings.celebrations))
      .catch(() => {
        // Une fête ne vaut pas un message d'erreur : on reste sur le défaut.
      });
  }, []);
}

/** Rien de tout cela ne bouge quand le système demande qu'on ne l'agite pas. */
function stillnessAsked(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

let layer: HTMLDivElement | null = null;

function celebrationLayer(): HTMLDivElement {
  if (layer?.isConnected) return layer;
  layer = document.createElement('div');
  layer.className = 'celebration';
  layer.setAttribute('aria-hidden', 'true');
  document.body.append(layer);
  return layer;
}

function pick<T>(values: readonly T[]): T {
  return values[Math.floor(Math.random() * values.length)] as T;
}

/**
 * Neuf cœurs qui montent du point touché — quatre en mode « douce ». La position est
 * celle du pointeur : la récompense sort du bouton qu'elle vient d'appuyer, pas d'un
 * coin de l'écran.
 */
export function hearts(x: number, y: number, palette: readonly string[] = HEART_COLORS): void {
  if (stillnessAsked()) return;

  const target = celebrationLayer();
  const spawned: HTMLElement[] = [];
  for (let index = 0; index < (intensity === 'joueuse' ? 9 : 4); index += 1) {
    const heart = document.createElement('span');
    heart.className = 'celebration__heart';
    heart.textContent = '♥';
    heart.style.left = `${x + (Math.random() * 30 - 15)}px`;
    heart.style.top = `${y - 10}px`;
    heart.style.color = pick(palette);
    heart.style.fontSize = `${14 + Math.random() * 14}px`;
    heart.style.setProperty('--dx', `${Math.random() * 120 - 60}px`);
    heart.style.setProperty('--rot', `${Math.random() * 60 - 30}deg`);
    target.append(heart);
    spawned.push(heart);
  }

  window.setTimeout(() => {
    for (const heart of spawned) heart.remove();
  }, 1000);
}

/**
 * Les pétales : pour un mois bouclé ou un trimestre déclaré, une fois. À l'appelant
 * de ne les demander qu'au passage de l'événement — pas à chaque ouverture de l'écran.
 */
export function petals(): void {
  if (stillnessAsked() || intensity !== 'joueuse') return;

  const target = celebrationLayer();
  const spawned: HTMLElement[] = [];
  for (let index = 0; index < 34; index += 1) {
    const petal = document.createElement('span');
    const width = 8 + Math.random() * 6;
    petal.className = 'celebration__petal';
    petal.style.left = `${Math.random() * 100}%`;
    petal.style.width = `${width}px`;
    petal.style.height = `${width * 1.5}px`;
    petal.style.background = pick(PETAL_COLORS);
    petal.style.animationDelay = `${Math.random() * 0.9}s`;
    petal.style.setProperty('--sx', `${Math.random() * 80 - 40}px`);
    petal.style.setProperty('--rot', `${360 + Math.random() * 540}deg`);
    target.append(petal);
    spawned.push(petal);
  }

  window.setTimeout(() => {
    for (const petal of spawned) petal.remove();
  }, 4200);
}

/**
 * Les pétales d'un événement qui n'arrive qu'une fois : le mois bouclé, le trimestre
 * déclaré. La clé porte l'événement (`ergo:mois-bouclé:2026-08`) et reste dans le
 * navigateur : rouvrir l'écran ne refête pas ce qui est déjà fêté. Un navigateur
 * sans stockage local ne perd rien d'essentiel — il refêtera, c'est tout.
 */
export function petalsOnce(key: string): void {
  try {
    if (window.localStorage.getItem(key) !== null) return;
    window.localStorage.setItem(key, new Date().toISOString());
  } catch {
    // Stockage refusé (navigation privée) : la fête a lieu, elle ne se retient pas.
  }
  petals();
}

const TOAST_MS = 2800;

/**
 * Le toast, lui, est du React : il se pose sur une carte précise, et c'est cette
 * carte qui sait où. `say` remplace le message en cours plutôt que d'empiler —
 * deux gestes rapprochés ne laissent qu'une phrase à lire, la dernière.
 */
export function useToast(): { toast: string | null; say: (text: string) => void } {
  const [toast, setToast] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);

  const say = useCallback((text: string) => {
    window.clearTimeout(timer.current);
    setToast(text);
    timer.current = window.setTimeout(() => setToast(null), TOAST_MS);
  }, []);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  return { toast, say };
}

/**
 * À poser dans un conteneur en `position: relative`. `role="status"` : le message est
 * la confirmation du geste, il doit s'entendre autant qu'il se voit.
 */
export function Toast({ children }: { children: ReactNode }) {
  return (
    <p className="toast" role="status">
      {children}
    </p>
  );
}
